import { useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { StatusBadge, Tabs, type DataTableColumn } from "@h2j-data-team/ui";
import type { AnalysisFieldChanges, AnalysisItemChanges } from "@repo/shared";

import { useLocale } from "@/hooks/use-locale";
import { cn } from "@/lib/utils";
import {
  deltaToneClass,
  formatBytes,
  formatSignedBytes,
  formatSignedCount,
} from "@/lib/health-format";

import { ReadOnlyTable } from "./read-only-table";

type ChangeGroup = "added" | "removed" | "modified";

const ITEM_TABS = ["variables", "dimensions", "measures"] as const;

const GROUP_TONE: Record<ChangeGroup, "success" | "danger" | "warning"> = {
  added: "success",
  removed: "danger",
  modified: "warning",
};

// Same emphasis the detail screen's inline summaries use for their numbers.
const SUMMARY_VALUE = { v: <span className="font-mono font-medium text-ink" /> };

function shownCount(changes: AnalysisFieldChanges | AnalysisItemChanges): number {
  return changes.added.length + changes.removed.length + changes.modified.length;
}

function totalCount(changes: AnalysisFieldChanges | AnalysisItemChanges): number {
  return shownCount(changes) + changes.truncatedCount;
}

function ChangesFooter({ changes }: { changes: AnalysisFieldChanges | AnalysisItemChanges }) {
  const { t } = useTranslation("analyses");
  return (
    <span className="text-[12px] text-dim">
      {t("compare.footer.showing", { shown: shownCount(changes), total: totalCount(changes) })}
      {changes.truncatedCount > 0 && (
        <span> · {t("compare.footer.truncated", { count: changes.truncatedCount })}</span>
      )}
    </span>
  );
}

function ChangeBadge({ group }: { group: ChangeGroup }) {
  const { t } = useTranslation("analyses");
  return <StatusBadge tone={GROUP_TONE[group]}>{t(`compare.kind.${group}`)}</StatusBadge>;
}

interface FieldRow {
  id: string;
  group: ChangeGroup;
  name: string;
  tableName: string | null;
  from: number | null;
  to: number | null;
  delta: number;
}

function FieldChangesTable({ changes }: { changes: AnalysisFieldChanges }) {
  const { t } = useTranslation("analyses");

  const rows = useMemo<FieldRow[]>(
    () => [
      ...changes.added.map((f) => ({
        id: `added-${f.name}`,
        group: "added" as const,
        name: f.name,
        tableName: f.tableName ?? null,
        from: null,
        to: f.byteSize,
        delta: f.byteSize,
      })),
      ...changes.removed.map((f) => ({
        id: `removed-${f.name}`,
        group: "removed" as const,
        name: f.name,
        tableName: f.tableName ?? null,
        from: f.byteSize,
        to: null,
        delta: -f.byteSize,
      })),
      ...changes.modified.map((f) => ({
        id: `modified-${f.name}`,
        group: "modified" as const,
        name: f.name,
        tableName: f.tableName ?? null,
        from: f.fromByteSize,
        to: f.toByteSize,
        delta: f.byteSizeDelta,
      })),
    ],
    [changes],
  );

  const columns: DataTableColumn<FieldRow>[] = [
    {
      id: "change",
      header: t("compare.col.group"),
      width: 130,
      cell: (row) => <ChangeBadge group={row.group} />,
    },
    {
      id: "field",
      header: t("compare.col.field"),
      rowHeader: true,
      cell: (row) => <span className="font-mono font-medium">{row.name}</span>,
    },
    {
      id: "table",
      header: t("compare.col.table"),
      cell: (row) => <span className="font-mono text-dim">{row.tableName ?? "—"}</span>,
    },
    {
      id: "range",
      header: t("compare.col.memoryRange"),
      align: "end",
      variant: "numeric",
      width: 220,
      cell: (row) => (
        <span className="text-dim">
          {t("compare.kpi.range", {
            from: row.from === null ? "—" : formatBytes(row.from),
            to: row.to === null ? "—" : formatBytes(row.to),
          })}
        </span>
      ),
    },
    {
      id: "delta",
      header: t("compare.col.memoryDelta"),
      align: "end",
      variant: "numeric",
      width: 140,
      // A new field always adds memory — no tone, it isn't a regression by itself.
      cell: (row) => (
        <span
          className={cn(
            "font-medium",
            row.group === "added" ? "text-ink" : deltaToneClass(row.delta, "lower-is-better"),
          )}
        >
          {formatSignedBytes(row.delta)}
        </span>
      ),
    },
  ];

  return (
    <ReadOnlyTable
      label={t("detail.tabs.fields")}
      columns={columns}
      rows={rows}
      getRowId={(row) => row.id}
      empty={t("compare.noChanges")}
      footer={<ChangesFooter changes={changes} />}
      minWidth={760}
    />
  );
}

interface ItemRow {
  id: string;
  group: ChangeGroup;
  label: string;
  change: string;
  broke: boolean;
  usageDelta: number | null;
}

function ItemChangesTable({ changes }: { changes: AnalysisItemChanges }) {
  const { t } = useTranslation("analyses");
  const { language } = useLocale();

  const rows = useMemo<ItemRow[]>(
    () => [
      ...(["added", "removed"] as const).flatMap((group) =>
        changes[group].map((item) => ({
          id: `${group}-${item.id}`,
          group,
          label: item.label,
          change: "—",
          broke: false,
          usageDelta: null,
        })),
      ),
      ...changes.modified.map((item) => {
        const bits = [
          ...(!item.brokenFrom && item.brokenTo ? [t("compare.change.broke")] : []),
          ...(item.brokenFrom && !item.brokenTo ? [t("compare.change.fixed")] : []),
          ...(item.definitionChanged ? [t("compare.change.definition")] : []),
        ];
        return {
          id: `modified-${item.id}`,
          group: "modified" as const,
          label: item.label,
          change: bits.length > 0 ? bits.join(" · ") : "—",
          broke: !item.brokenFrom && item.brokenTo,
          usageDelta: item.usedInCountDelta,
        };
      }),
    ],
    [changes, t],
  );

  const columns: DataTableColumn<ItemRow>[] = [
    {
      id: "group",
      header: t("compare.col.group"),
      width: 130,
      cell: (row) => <ChangeBadge group={row.group} />,
    },
    {
      id: "item",
      header: t("compare.col.item"),
      rowHeader: true,
      cell: (row) => <span className="font-mono font-medium">{row.label}</span>,
    },
    {
      id: "change",
      header: t("compare.col.change"),
      cell: (row) => (
        <span className={row.broke ? "text-status-danger-foreground" : "text-dim"}>
          {row.change}
        </span>
      ),
    },
    {
      id: "usageDelta",
      header: t("compare.col.usageDelta"),
      align: "end",
      variant: "numeric",
      width: 140,
      cell: (row) => (
        <span className="text-dim">
          {row.usageDelta === null ? "—" : formatSignedCount(row.usageDelta, language)}
        </span>
      ),
    },
  ];

  return (
    <ReadOnlyTable
      label={t("compare.col.item")}
      columns={columns}
      rows={rows}
      getRowId={(row) => row.id}
      empty={t("compare.noChanges")}
      footer={<ChangesFooter changes={changes} />}
      minWidth={640}
    />
  );
}

function ChangesSummaryLine({
  changes,
  bytesDelta,
}: {
  changes: AnalysisFieldChanges | AnalysisItemChanges;
  bytesDelta?: string;
}) {
  const { t } = useTranslation("analyses");
  return (
    <p className="text-sm text-dim">
      <Trans
        t={t}
        i18nKey={bytesDelta ? "compare.inlineSummary.fields" : "compare.inlineSummary.items"}
        values={{
          added: changes.added.length,
          removed: changes.removed.length,
          modified: changes.modified.length,
          bytes: bytesDelta,
        }}
        components={SUMMARY_VALUE}
      />
    </p>
  );
}

interface CompareItemChangesProps {
  fieldChanges: AnalysisFieldChanges;
  itemChanges: {
    variables: AnalysisItemChanges;
    dimensions: AnalysisItemChanges;
    measures: AnalysisItemChanges;
  };
}

/** One tab per collection: inline summary + the added/removed/modified table. */
export function CompareItemChanges({ fieldChanges, itemChanges }: CompareItemChangesProps) {
  const { t } = useTranslation("analyses");
  const [tab, setTab] = useState("fields");

  // Net memory across the field changes that are actually listed — truncated
  // rows are excluded, which the table footer calls out.
  const fieldBytesDelta =
    fieldChanges.added.reduce((sum, f) => sum + f.byteSize, 0) -
    fieldChanges.removed.reduce((sum, f) => sum + f.byteSize, 0) +
    fieldChanges.modified.reduce((sum, f) => sum + f.byteSizeDelta, 0);

  const tabLabel = (name: string, count: number) => (
    <>
      {name}
      <span className="ml-1.5 font-mono text-[11px] text-faint tabular-nums">{count}</span>
    </>
  );

  return (
    <Tabs
      label={t("compare.changesLabel")}
      value={tab}
      onValueChange={setTab}
      items={[
        {
          value: "fields",
          label: tabLabel(t("detail.tabs.fields"), totalCount(fieldChanges)),
          content: (
            <div className="space-y-4">
              <ChangesSummaryLine
                changes={fieldChanges}
                bytesDelta={formatSignedBytes(fieldBytesDelta)}
              />
              <FieldChangesTable changes={fieldChanges} />
            </div>
          ),
        },
        ...ITEM_TABS.map((name) => ({
          value: name,
          label: tabLabel(t(`detail.tabs.${name}`), totalCount(itemChanges[name])),
          content: (
            <div className="space-y-4">
              <ChangesSummaryLine changes={itemChanges[name]} />
              <ItemChangesTable changes={itemChanges[name]} />
            </div>
          ),
        })),
      ]}
    />
  );
}
